import client from './client';
import { getCache, setCache } from '../utils/apiCache';

export interface Product {
    id: number;
    slug: string;
    title_ar: string;
    title_fr: string;
    categories: Category[];
    category?: Category; // Legacy support (optional)
    price: number;
    discount_price: number | null;
    currency: string;
    stock: number;
    unit_name_ar?: string;
    unit_name_fr?: string;
    sku: string;
    images: string[];
    badges: string[] | string; // API sometimes returns string instead of array
    badges_ar?: string[] | string; // API sometimes returns string instead of array
    short_desc_ar: string;
    short_desc_fr: string;
    description_ar?: string; // Full description (optional, for future backend support)
    description_fr?: string; // Full description (optional, for future backend support)
    ingredients_ar: string[] | string; // API sometimes returns string instead of array
    ingredients_fr: string[] | string; // API sometimes returns string instead of array
    variants: ProductVariant[];
    product_url?: string;
    how_to_use_it_audio?: string;
    tags: Tag[];
}

export interface Tag {
    id: number;
    slug: string;
    name_ar: string;
    name_fr: string;
}

export interface ProductVariant {
    id: number;
    product_id: number;
    name: string;
    name_ar?: string;
    price: string; // Decimal string from Laravel
    created_at: string;
    updated_at: string;
}

export interface Category {
    id: number;
    slug: string;
    name_ar: string;
    name_fr: string;
    image?: string;
    sort_order: number;
}

export const getProducts = async (params?: any) => {
    const cacheKey = params ? `products_${JSON.stringify(params)}` : 'products_all';
    const cachedData = getCache<Product[]>(cacheKey);
    if (cachedData) return cachedData;

    const response = await client.get<Product[]>('/products', { params });
    setCache(cacheKey, response.data);
    return response.data;
};

export const getProductBySlug = async (slug: string) => {
    const cacheKey = `product_${slug}`;
    const cachedData = getCache<Product>(cacheKey);
    if (cachedData) return cachedData;

    const response = await client.get<Product[]>(`/products?slug=${slug}`);
    const product = response.data[0];
    if (product) {
        setCache(cacheKey, product);
    }
    return product;
};

export const getCategories = async () => {
    const cacheKey = 'categories';
    const cachedData = getCache<Category[]>(cacheKey);
    if (cachedData) return cachedData;

    const response = await client.get<Category[]>('/categories');
    setCache(cacheKey, response.data);
    return response.data;
};

export const getTags = async () => {
    const cacheKey = 'tags';
    const cachedData = getCache<Tag[]>(cacheKey);
    if (cachedData) return cachedData;

    const response = await client.get<Tag[]>('/tags');
    setCache(cacheKey, response.data);
    return response.data;
};

export const getTestimonials = async (productId?: number) => {
    const cacheKey = productId ? `testimonials_product_${productId}` : 'testimonials_general';
    const cachedData = getCache<any>(cacheKey);
    if (cachedData) return cachedData;

    const params: any = {};
    if (productId) {
        params.product_id = productId;
    }

    const response = await client.get('/testimonials', { params });
    setCache(cacheKey, response.data);
    return response.data;
};
